/**
 * EnvironmentDetector.js
 * -----------------------
 * ES6 Module utility for detecting execution environment (SSR vs. Client).
 * Provides safe gating of browser-specific code, hydration helpers, and error tracking.
 *
 * Responsibilities:
 * - Determine context: `isServerSide()` or `isClientSide()`
 * - Provide environment name for logging
 * - Execute callbacks conditionally via `clientOnly()` and `serverOnly()`
 * - Offer detailed environment info, runtime checks, and self-tests
 * - Track critical errors using ErrorHandler
 *
 * Features:
 * - ES module syntax with named/default exports
 * - SSR-safe: no top-level `window` or `document` usage outside guards
 * - ErrorHandler integration for tracking detection failures
 * - Fallback no-op handler when ErrorHandler is unavailable
 *
 * Limitations:
 * - Assumes `window` and `document` are unavailable in SSR
 * - Error data is stored in memory (non-persistent)
 * - Does not detect specific runtimes beyond browser vs. non-browser
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

// Determine SSR vs. Client context
const isServerSide = typeof window === 'undefined' || typeof document === 'undefined';
const isClientSide = !isServerSide;

export default class EnvironmentDetector {
  static _errorHandler = null;

  /**
   * Lazy-init shared ErrorHandler
   */
  static _initErrorHandler() {
    if (this._errorHandler) return;
    try {
      this._errorHandler = new ErrorHandler({ module: 'EnvironmentDetector', maxErrors: 10 });
    } catch {
      // Fallback no-op handler
      this._errorHandler = {
        handleError: () => {},
        handleFatalError: () => {},
        getRecentErrors: () => [],
        hasFatalErrors: () => false,
        getErrorStats: () => ({ totalErrors: 0, fatalErrors: 0 })
      };
    }
  }

  /**
   * True if running in non-browser environment (SSR)
   */
  static isServerSide() {
    try {
      return isServerSide;
    } catch (err) {
      this._initErrorHandler();
      this._errorHandler.handleError('Error detecting server-side environment', err, { operation: 'isServerSide' });
      return true;
    }
  }

  /**
   * True if running in browser (Client)
   */
  static isClientSide() {
    try {
      return isClientSide;
    } catch (err) {
      this._initErrorHandler();
      this._errorHandler.handleError('Error detecting client-side environment', err, { operation: 'isClientSide' });
      return false;
    }
  }

  /**
   * Returns 'server' or 'client'
   */
  static getEnvironmentName() {
    return this.isServerSide() ? 'server' : 'client';
  }

  /**
   * Executes fn only on client; returns fallback otherwise
   */
  static clientOnly(fn, fallback = null) {
    this._initErrorHandler();
    if (typeof fn !== 'function') {
      this._errorHandler.handleError('clientOnly called with non-function', new Error(), { operation: 'clientOnly' });
      return fallback;
    }
    if (this.isClientSide()) {
      try {
        return fn();
      } catch (err) {
        this._errorHandler.handleError('Error in clientOnly fn', err, { operation: 'clientOnly' });
      }
    }
    return fallback;
  }

  /**
   * Executes fn only on server; returns fallback otherwise
   */
  static serverOnly(fn, fallback = null) {
    this._initErrorHandler();
    if (typeof fn !== 'function') {
      this._errorHandler.handleError('serverOnly called with non-function', new Error(), { operation: 'serverOnly' });
      return fallback;
    }
    if (this.isServerSide()) {
      try {
        return fn();
      } catch (err) {
        this._errorHandler.handleError('Error in serverOnly fn', err, { operation: 'serverOnly' });
      }
    }
    return fallback;
  }

  /**
   * Returns detailed environment info and runtime checks
   */
  static getDetailedEnvironmentInfo() {
    this._initErrorHandler();
    const info = {
      environment: this.getEnvironmentName(),
      isServerSide: this.isServerSide(),
      isClientSide: this.isClientSide(),
      timestamp: new Date().toISOString(),
      runtimeChecks: {}
    };
    try {
      info.runtimeChecks.hasWindow = typeof window !== 'undefined';
      info.runtimeChecks.hasDocument = typeof document !== 'undefined';
      info.runtimeChecks.hasNavigator = typeof navigator !== 'undefined';
      info.runtimeChecks.hasProcess = typeof process !== 'undefined';
    } catch (err) {
      this._errorHandler.handleError('Runtime checks failed', err, { operation: 'getDetailedEnvironmentInfo' });
    }
    return info;
  }

  /**
   * Executes a suite of self-tests for environment detection
   */
  static runEnvironmentTests() {
    this._initErrorHandler();
    const tests = { timestamp: new Date().toISOString(), results: {}, summary: { passed: 0, failed: 0, total: 0 } };
    // Consistency test
    try {
      const a = this.isServerSide(); const b = this.isServerSide();
      const c = this.isClientSide(); const d = this.isClientSide();
      const passed = a === b && c === d && a !== c;
      tests.results.consistency = { passed, details: { server: a, client: c } };
      tests.summary.total++;
      tests.summary[passed ? 'passed' : 'failed']++;
    } catch {
      tests.results.consistency = { passed: false };
      tests.summary.failed++; tests.summary.total++;
    }
    // Name test
    try {
      const envName = this.getEnvironmentName();
      const expected = this.isServerSide() ? 'server' : 'client';
      const passed = envName === expected;
      tests.results.name = { passed, details: { envName, expected } };
      tests.summary.total++; tests.summary[passed ? 'passed' : 'failed']++;
    } catch {
      tests.results.name = { passed: false };
      tests.summary.failed++; tests.summary.total++;
    }
    return tests;
  }

  /**
   * Recent detection errors
   */
  static getRecentErrors(minutes = 5) {
    this._initErrorHandler();
    return this._errorHandler.getRecentErrors(minutes);
  }

  /**
   * Whether fatal errors occurred
   */
  static hasCriticalIssues() {
    this._initErrorHandler();
    return this._errorHandler.hasFatalErrors();
  }

  /**
   * Returns error stats
   */
  static getErrorStats() {
    this._initErrorHandler();
    return this._errorHandler.getErrorStats();
  }
}
